/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {Directive, EventEmitter, Inject, Input, OnDestroy, Output, TemplateRef, ViewContainerRef} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';

import {IF_ACTIVE_ID, IfActiveService} from './if-active.service';
/**
 * *******
 * 
 * \@class IfActiveDirective
 * 
 * \@description 
 * A structural directive that controls whether or not the associated TemplateRef is instantiated or not.
 * It makes use of a Component instance level service: IfActiveService to maintain state between itself and
 * the component using it in the component template.
 * 
 */
export class IfActiveDirective implements OnDestroy {
private subscription: Subscription;
private wasActive: boolean = false;
/**
 * @param {?} ifActiveService
 * @param {?} id
 * @param {?} template
 * @param {?} container
 */
constructor(private ifActiveService: IfActiveService,
private id: number,
private template: TemplateRef<any>,
private container: ViewContainerRef) {
        this.checkAndUpdateView(ifActiveService.current);

        this.subscription = this.ifActiveService.currentChange.subscribe((newCurrentId) => {
            this.checkAndUpdateView(newCurrentId);
        });
    }
/**
 * @param {?} currentId
 * @return {?}
 */
private checkAndUpdateView(currentId: number) {
        const /** @type {?} */ isNowActive = currentId === this.id;
        // only emit if the new active state is changed since last time.
        if (isNowActive !== this.wasActive) {
            this.updateView(isNowActive);
            this.activeChange.emit(isNowActive);
            this.wasActive = isNowActive;
        }
    }
/**
 * ******
 * \@function active
 * 
 * \@description 
 * A setter that updates IfActiveService.active with value.
 * 
 * @param {?} value
 * @return {?}
 */
public set active(value: boolean) {
        if (value) {
            this.ifActiveService.current = this.id;
        }
    }
/**
 * *******
 * \@property activeChange
 * 
 * \@description 
 * An event emitter that emits when the active property is set to allow for 2way binding when the directive is
 * used with de-structured / de-sugared syntax.
 * 
 */
activeChange: EventEmitter<boolean> = new EventEmitter<boolean>(false);
/**
 * *****
 * \@function active
 * 
 * \@description 
 * A getter that returns the current IfActiveService.active value.
 * @return {?}
 */
public get active() {
        return this.ifActiveService.current === this.id;
    }
/**
 * ******
 * \@function updateView
 * 
 * \@description 
 * Function that takes a any value and either created an embedded view for the associated ViewContainerRef or,
 * Clears all views from the ViewContainerRef
 * @param {?} value
 * @return {?}
 */
public updateView(value: boolean) {
        if (value) {
            this.container.createEmbeddedView(this.template);
        } else {
            this.container.clear();
        }
    }
/**
 * @return {?}
 */
ngOnDestroy() {
        this.subscription.unsubscribe();
    }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: "[clrIfActive]"}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: IfActiveService, },
{type: undefined, decorators: [{ type: Inject, args: [IF_ACTIVE_ID, ] }, ]},
{type: TemplateRef, },
{type: ViewContainerRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'active': [{ type: Input, args: ["clrIfActive", ] },],
'activeChange': [{ type: Output, args: ["clrIfActiveChange", ] },],
};
}

function IfActiveDirective_tsickle_Closure_declarations() {
/** @type {?} */
IfActiveDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
IfActiveDirective.ctorParameters;
/** @type {?} */
IfActiveDirective.propDecorators;
/** @type {?} */
IfActiveDirective.prototype.subscription;
/** @type {?} */
IfActiveDirective.prototype.wasActive;
/**
 * *******
 * \@property activeChange
 * 
 * \@description 
 * An event emitter that emits when the active property is set to allow for 2way binding when the directive is
 * used with de-structured / de-sugared syntax.
 * 
 * @type {?}
 */
IfActiveDirective.prototype.activeChange;
/** @type {?} */
IfActiveDirective.prototype.ifActiveService;
/** @type {?} */
IfActiveDirective.prototype.id;
/** @type {?} */
IfActiveDirective.prototype.template;
/** @type {?} */
IfActiveDirective.prototype.container;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
